import React, { useRef, useState, useEffect } from 'react';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { useUserContext } from '@fff-web/fff-utilities';
import { trackPageLoad, identify } from '@fff-web/fff-analytics';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import { redirectToBaseSite } from '../../utils/redirectMethods';
import env from '../MyAccount/utils/env';
import { Plans, MOBILE } from '../constants';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import useScreenMode from '../../hooks/useScreenMode';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { updateSubscription } from '../../services/subscription';

import SubscribeTodayDisclaimer from './SubscribeTodayDisclaimer';
import MobileLayout from './MobileLayout';
import DesktopLayout from './DesktopLayout';
import Layout from '../../components/Layout';
import { sendActivateSegmentEventAfterResponse } from '../../Events';
import AllErrorMessage from '../../components/AllErrorMessage';

import MobileBg from './images/subscribe-today-bottom-bg@2x.png';
import styles from './subscribe-today.module.scss';

const THANKS_PAGE = '/reactivate-thanks';
const ROLE_CLUB = 'CLUB';

export const SubscribeToday = ({ history }) => {
  const socialCaptchaRef = useRef(null);
  const screenMode = useScreenMode();
  const [selectedPlanCode, setSelectedPlanCode] = useState(Plans.SEASONAL);
  const { userInfo } = useUserContext();
  const isClubMember = userInfo?.user?.role === ROLE_CLUB || false;

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(history, THANKS_PAGE);

  const handleChange = (membership) => setSelectedPlanCode(membership);

  const handleChangeSubscription = () => {
    socialCaptchaRef.current.resetCaptcha();
    socialCaptchaRef.current
      .execute({ async: true })
      .then(({ response: captchaToken }) => {
        setLoading(true);
        handleActivateSubscription(captchaToken, selectedPlanCode);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error processing you request:', err);
      });
  };

  const handleActivateSubscription = (captchaToken, upgradingPlan) => {
    let response = null;

    // make call to activate subscription
    updateSubscription(upgradingPlan, userInfo?.user?.planCode, [], captchaToken)
      .then((updateSubscriptionResponse) => {
        response = updateSubscriptionResponse;
        const status = getPlanChangeCompletedEventStatusFromResponse(response);
        const eventsToFire = [
          sendActivateSegmentEventAfterResponse(status, 'Confirm Purchase', 'Trial'),
        ];
        if (status === 'success') {
          eventsToFire.push(identify({ traits: { planCode: upgradingPlan } }));
        }
        return Promise.resolve(eventsToFire);
      })
      .then(() => {
        handlePlanChangeResponse(response);
      });
  };

  const purchaseStyle =
    screenMode === MOBILE
      ? {
          backgroundImage: `url(${MobileBg})`,
          backgroundPosition: 'center bottom',
          backgroundSize: 'cover',
        }
      : {};

  // allow access to club members
  useEffect(() => {
    if (isClubMember) {
      trackPageLoad({
        pathName: '/subscribe-today',
        properties: {
          role: (userInfo.user.role || '').toLowerCase(),
          subscription_state: (userInfo.user.subscriptionState || '').toLowerCase(),
        },
      }).then();
    } else {
      redirectToBaseSite();
    }
  }, [isClubMember]);

  return (
    <Layout
      pageTitle="Subscribe Today"
      isLoading={isLoading}
      showError={showRetry}
      ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
    >
      <MobileLayout selectedPlan={selectedPlanCode} handlePlanChange={handleChange} />
      <DesktopLayout selectedPlan={selectedPlanCode} handlePlanChange={handleChange} />
      <section className={styles.purchase} style={purchaseStyle} data-testid="purchase-container">
        <SubscribeTodayDisclaimer />
        <button
          type="button"
          className={styles.confirm}
          onClick={handleChangeSubscription}
        >
          CONFIRM PURCHASE
        </button>
        <HCaptcha
          sitekey={env.hCaptchaSiteKey}
          onVerify={() => undefined}
          size="invisible"
          ref={socialCaptchaRef}
        />
      </section>
      {/* <HowItWorks /> */}
      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={handleChangeSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
};

export default SubscribeToday;
